import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { get, post } from '../lib/api.ts'
import { useMe } from '../lib/hooks.ts'
import { Button, ErrorNote, Sheet, Spinner } from '../components/ui.tsx'
import { t } from '../lib/i18n.ts'

const RETURN_KEY = 'subsindico:oauth-consent'

/**
 * OAuth consent (ADR 0018): an MCP client (claude.ai, ChatGPT, Cursor…) asked to act for the signed-in user.
 * The server already validated the client and the redirect URI; this page only collects the decision.
 */
export function OAuthConsent() {
  const me = useMe()
  const nav = useNavigate()
  // The query survives the login round-trip through sessionStorage (a per-viewer convenience, never trusted by the server).
  const params = useMemo(() => {
    const q = new URLSearchParams(location.search)
    if (q.get('client_id')) {
      try {
        sessionStorage.setItem(RETURN_KEY, q.toString())
      } catch {
        /* private window */
      }
      return q
    }
    try {
      const saved = sessionStorage.getItem(RETURN_KEY)
      return new URLSearchParams(saved ?? '')
    } catch {
      return q
    }
  }, [])
  const clientId = params.get('client_id') ?? ''
  const redirectUri = params.get('redirect_uri') ?? ''
  const redirectHost = useMemo(() => {
    try {
      return new URL(redirectUri).host
    } catch {
      return ''
    }
  }, [redirectUri])
  const loggedOut = me.isSuccess && !me.data.user
  useEffect(() => {
    if (loggedOut) nav({ to: '/entrar', search: { next: '/oauth/consent' } as never })
  }, [loggedOut])
  const client = useQuery({ queryKey: ['oauth-client', clientId], queryFn: () => get<{ clientId: string; name: string; redirectHosts: string[]; documentHost: string | null }>(`/api/oauth/client?id=${encodeURIComponent(clientId)}`), enabled: Boolean(clientId && me.data?.user) })
  const decide = useMutation({
    mutationFn: (decision: 'allow' | 'deny') => post<{ redirectTo: string }>('/api/oauth/approve', { clientId, redirectUri, codeChallenge: params.get('code_challenge'), state: params.get('state'), scope: params.get('scope'), decision }),
    onSuccess: (r) => {
      try {
        sessionStorage.removeItem(RETURN_KEY)
      } catch {
        /* ignore */
      }
      location.assign(r.redirectTo)
    },
  })
  if (!clientId || !redirectUri) {
    return (
      <main className="mx-auto max-w-lg px-4 py-10">
        <Sheet className="p-5">
          <h1 className="text-xl font-bold">{t('Pedido de conexão incompleto')}</h1>
          <p className="mt-2 text-tinta-2">{t('Abra a conexão de novo a partir do seu app de IA. Se o problema continuar, o app pode não estar usando OAuth com PKCE.')}</p>
          <Link to="/" className="mt-4 inline-block font-semibold text-caneta underline">{t('Voltar ao início')}</Link>
        </Sheet>
      </main>
    )
  }
  return (
    <main className="mx-auto max-w-lg px-4 py-10">
      <p className="mb-2 text-sm font-semibold text-caneta">{t('Subsíndico')}</p>
      <Sheet className="p-5">
        {me.isLoading || client.isLoading ? <Spinner /> : client.isError ? <ErrorNote error={client.error} /> : (
          <>
            <h1 className="text-xl font-bold">{t('Conectar')}{' '}{client.data?.name ?? t('o app')}{' '}{t('ao Subsíndico')}</h1>
            <p className="mt-3 text-tinta-2">
              {t('Este app poderá ler, em seu nome, o que você já vê nos seus condomínios: documentos, avisos, reservas, prazos, finanças, portaria e consumo. Para abrir chamados, publicar avisos ou reservar espaços ele sempre mostra uma prévia e pede a sua confirmação.')}
            </p>
            {client.data?.documentHost && (
              <p className="mt-3 text-sm text-tinta-2">{t('Este app é identificado por')}{' '}<strong className="text-tinta">{client.data.documentHost}</strong>.</p>
            )}
            <p className="mt-3 text-sm text-tinta-2">{t('Entrou como')}{' '}<strong className="text-tinta">{me.data?.user?.name}</strong>{t('. Depois de permitir, você volta para')}{' '}<strong className="text-tinta">{redirectHost}</strong>{t('. Você pode desconectar quando quiser em Meu perfil → Assistentes de IA.')}</p>
            <ErrorNote error={decide.error} />
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="quiet" disabled={decide.isPending} onClick={() => decide.mutate('deny')}>{t('Negar')}</Button>
              <Button busy={decide.isPending} onClick={() => decide.mutate('allow')}>{t('Permitir')}</Button>
            </div>
          </>
        )}
      </Sheet>
    </main>
  )
}
